import { CombinedGraphQLErrors, type ErrorLike } from "@apollo/client";
import { type TFunction } from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

// Backend error codes — keep in sync with ServiceException factory methods.
const serviceErrorCodes = [
  "error.auth.email-link-expired", // t("error.auth.email-link-expired")
  "error.auth.email-link-not-found", // t("error.auth.email-link-not-found")
  "error.auth.email-link-used", // t("error.auth.email-link-used")
  "error.auth.not-authenticated", // t("error.auth.not-authenticated")
  "error.auth.not-authorized", // t("error.auth.not-authorized")
  "error.auth.signup-name-required", // t("error.auth.signup-name-required")
  "error.ingredient.deleted", // t("error.ingredient.deleted")
  "error.ingredient.not-found", // t("error.ingredient.not-found")
  "error.ingredient.price-invalid", // t("error.ingredient.price-invalid")
  "error.ingredient.price-unit-not-weight", // t("error.ingredient.price-unit-not-weight")
  "error.producer.deleted", // t("error.producer.deleted")
  "error.producer.not-found", // t("error.producer.not-found")
  "error.recipe.circular-dependency", // t("error.recipe.circular-dependency")
  "error.recipe.circular-self-reference", // t("error.recipe.circular-self-reference")
  "error.recipe.deleted", // t("error.recipe.deleted")
  "error.recipe.not-found", // t("error.recipe.not-found")
  "error.recipe.not-published", // t("error.recipe.not-published")
  "error.subscription.access-locked", // t("error.subscription.access-locked")
  "error.subscription.no-customer", // t("error.subscription.no-customer")
  "error.subscription.stripe-unavailable", // t("error.subscription.stripe-unavailable")
  "error.team.cannot-join-account-exists", // t("error.team.cannot-join-account-exists")
  "error.team.cannot-remove-last-admin", // t("error.team.cannot-remove-last-admin")
  "error.team.invitation-already-exists", // t("error.team.invitation-already-exists")
  "error.team.invitation-email-undeliverable", // t("error.team.invitation-email-undeliverable")
  "error.team.invitation-not-found", // t("error.team.invitation-not-found")
  "error.team.member-not-found", // t("error.team.member-not-found")
  "error.team.not-found", // t("error.team.not-found")
  "error.team.settings-invalid", // t("error.team.settings-invalid")
  "error.unknown", // t("error.unknown")
] as const;

const serviceErrorExtensionsSchema = z.object({
  errorCode: z.enum(serviceErrorCodes).catch("error.unknown"),
  errorParams: z.record(z.string(), z.string().optional()).optional(),
});

export type ServiceErrorCode = (typeof serviceErrorCodes)[number];

export type ServiceErrorDetails = {
  code: ServiceErrorCode;
  params: Record<string, string | undefined> | undefined;
};

export const extractServiceError = (error: unknown): ServiceErrorDetails | undefined => {
  if (!CombinedGraphQLErrors.is(error)) return undefined;
  const graphQLError = error.errors[0];
  if (!graphQLError) return undefined;
  const parsed = serviceErrorExtensionsSchema.safeParse(graphQLError.extensions);
  if (!parsed.success) return undefined;
  return { code: parsed.data.errorCode, params: parsed.data.errorParams };
};

export const translateServiceError = (error: unknown, t: TFunction): string | undefined => {
  const details = extractServiceError(error);
  if (!details) return undefined;
  return t(details.code, details.params);
};

export const useServiceError = (error: ErrorLike | undefined): string | undefined => {
  const { t } = useTranslation();
  return translateServiceError(error, t);
};

export const useServiceErrorDetails = (error: ErrorLike | undefined): ServiceErrorDetails | undefined =>
  useMemo(() => extractServiceError(error), [error]);
